import type { editor, IPosition, IRange } from "monaco-editor";

import { Monaco } from "@monaco-editor/react";

import {
  createEntityTemplateValidator,
  createLiquidTemplateValidator,
} from "./monaco-validators";

export type PickerType = "date" | "entity" | "liquid";

export interface PickerState {
  left: number;
  open: boolean;
  range: IRange | null;
  top: number;
  type: PickerType;
}

/**
 * Calculates the position for opening a picker at the cursor position
 */
export const calculatePickerPosition = (
  editorInstance: editor.IStandaloneCodeEditor,
  position: IPosition,
): { left: number; top: number } | null => {
  const cursorCoords = editorInstance.getScrolledVisiblePosition(position);
  if (!cursorCoords) return null;

  const editorDomNode = editorInstance.getDomNode();
  if (!editorDomNode) return null;

  const rect = editorDomNode.getBoundingClientRect();

  return {
    left: rect.left + cursorCoords.left,
    top: rect.top + cursorCoords.top + cursorCoords.height,
  };
};

/**
 * Creates a range for the trigger characters (<< or {{)
 */
export const createTriggerRange = (
  monacoInstance: Monaco,
  position: IPosition,
  triggerLength: number,
): IRange => {
  return new monacoInstance.Range(
    position.lineNumber,
    position.column - triggerLength,
    position.lineNumber,
    position.column,
  );
};

/**
 * Creates a range for insertion at the current position
 */
export const createInsertionRange = (
  editorInstance: editor.IStandaloneCodeEditor,
  monacoInstance: Monaco,
): IRange | null => {
  const position = editorInstance.getPosition();
  if (!position) return null;

  const selection = editorInstance.getSelection();

  // Replace selection if present; otherwise insert at caret (0-length range)
  return selection && !selection.isEmpty()
    ? selection
    : new monacoInstance.Range(
        position.lineNumber,
        position.column,
        position.lineNumber,
        position.column,
      );
};

/**
 * Inserts text at the specified range in the editor
 */
export const insertTextAtRange = (
  editorInstance: editor.IStandaloneCodeEditor,
  text: string,
  range: IRange,
): void => {
  editorInstance.executeEdits("picker-insert", [
    {
      forceMoveMarkers: true,
      range,
      text,
    },
  ]);
  editorInstance.focus();
};

/**
 * Sets up trigger detection for entity (<<) and date ({{) pickers
 */
export const setupPickerTriggers = (
  editorInstance: editor.IStandaloneCodeEditor,
  monacoInstance: Monaco,
  options: {
    isPickerOpen: () => boolean;
    onTrigger: (
      editorInstance: editor.IStandaloneCodeEditor,
      position: IPosition,
      range: IRange,
      type: PickerType,
    ) => void;
  },
): (() => void) => {
  const { isPickerOpen, onTrigger } = options;

  const disposable = editorInstance.onDidChangeModelContent(() => {
    // Don't pop up repeatedly while picker is open
    if (isPickerOpen()) return;

    const position = editorInstance.getPosition();
    if (!position) return;

    const model = editorInstance.getModel();
    if (!model) return;

    const line = model.getLineContent(position.lineNumber);
    const beforeCursor = line.slice(0, position.column - 1);

    // Check for entity trigger `<<`
    if (beforeCursor.endsWith("<<")) {
      const range = createTriggerRange(monacoInstance, position, 2);
      onTrigger(editorInstance, position, range, "entity");
      return;
    }

    // Check for date trigger `{{`
    if (beforeCursor.endsWith("{{")) {
      const range = createTriggerRange(monacoInstance, position, 2);
      onTrigger(editorInstance, position, range, "date");
      return;
    }
  });

  return () => disposable.dispose();
};

/**
 * Sets up context menu actions for inserting entities and date snippets
 */
export const setupContextMenuActions = (
  editorInstance: editor.IStandaloneCodeEditor,
  monacoInstance: Monaco,
  options: {
    onOpenPicker: (
      editorInstance: editor.IStandaloneCodeEditor,
      position: IPosition,
      range: IRange,
      type: PickerType,
    ) => void;
  },
): void => {
  const { onOpenPicker } = options;

  // Ensure right click moves cursor (Monaco often does, but this makes it deterministic)
  editorInstance.onMouseDown((e) => {
    if (e.event.rightButton && e.target.position) {
      editorInstance.setPosition(e.target.position);
    }
  });

  editorInstance.addAction({
    contextMenuGroupId: "navigation",
    contextMenuOrder: 1.5,
    id: "insert-entity",
    keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyE],
    label: "Insert entity…",
    run: () => {
      const model = editorInstance.getModel();
      const position = editorInstance.getPosition();
      if (!model || !position) return;

      const range = createInsertionRange(editorInstance, monacoInstance);
      if (!range) return;

      onOpenPicker(editorInstance, position, range, "entity");
    },
  });

  editorInstance.addAction({
    contextMenuGroupId: "navigation",
    contextMenuOrder: 1.6,
    id: "insert-date",
    keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyD],
    label: "Insert date snippet…",
    run: () => {
      const model = editorInstance.getModel();
      const position = editorInstance.getPosition();
      if (!model || !position) return;

      const range = createInsertionRange(editorInstance, monacoInstance);
      if (!range) return;

      onOpenPicker(editorInstance, position, range, "date");
    },
  });

  editorInstance.addAction({
    contextMenuGroupId: "navigation",
    contextMenuOrder: 1.6,
    id: "insert-liquid",
    keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyL],
    label: "Insert liquid snippet…",
    run: () => {
      const model = editorInstance.getModel();
      const position = editorInstance.getPosition();
      if (!model || !position) return;

      const range = createInsertionRange(editorInstance, monacoInstance);
      if (!range) return;

      onOpenPicker(editorInstance, position, range, "liquid");
    },
  });
};

/**
 * Sets up validators for entity templates and liquid templates
 */
export const setupValidators = (
  editorInstance: editor.IStandaloneCodeEditor,
  monacoInstance: Monaco,
  options: {
    language?: "json" | "liquid" | "plaintext";
  },
): void => {
  const { language = "plaintext" } = options;

  const model = editorInstance.getModel();
  if (!model) return;

  const validate = createEntityTemplateValidator(
    editorInstance,
    monacoInstance,
  );
  model.onDidChangeContent(() => {
    validate();
  });

  // Add liquid template validator only if language is not "liquid"
  if (language !== "liquid") {
    const validateLiquid = createLiquidTemplateValidator(
      editorInstance,
      monacoInstance,
    );
    model.onDidChangeContent(async () => {
      // Run entity validator first, then liquid validator
      validate();
      await validateLiquid();
    });
  }
};

/**
 * Handles editor validation markers and syncs them with form errors
 */
export const handleEditorValidation = (
  markers: editor.IMarker[],
  options: {
    clearErrors: (fieldName: string) => void;
    fieldName: null | string;
    setError: (
      fieldName: string,
      error: { message: string; type: string },
    ) => void;
  },
): void => {
  const { clearErrors, fieldName, setError } = options;

  if (!fieldName) return;

  if (markers.length > 0) {
    markers.forEach((marker) => {
      setError(fieldName, {
        message: String(marker.message),
        type: "manual",
      });
    });
  } else {
    clearErrors(fieldName);
  }
};
